import { Lesson, LessonBlock } from '@/lib/types';
import { useEffect, useRef } from 'react';

interface LessonViewerProps {
    lesson: Lesson;
    onComplete: () => void;
    isCompleted: boolean;
    onNext?: () => void;
    onPrevious?: () => void;
    onTimeUpdate?: (seconds: number) => void;
}

function toEmbedUrl(url: string): string {
    if (url.includes('youtu.be/')) {
        const id = url.split('youtu.be/')[1]?.split(/[?&]/)[0];
        return id ? `https://www.youtube.com/embed/${id}` : url;
    }
    if (url.includes('watch?v=')) {
        return url.replace('watch?v=', 'embed/');
    }
    return url;
}

function sanitizeHtml(html: string): string {
    // Minimal sanitizer: strip script tags; full DOMPurify can be added later.
    return html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
}

function BlockRenderer({ block }: { block: LessonBlock }) {
    const payload = (block.payload || {}) as Record<string, unknown>;

    if (block.block_type === 'rich_text') {
        const html = String(payload.html || '');
        if (!html.trim()) return null;
        return (
            <div className="mb-8 overflow-x-auto">
                <div
                    className="prose prose-sm max-w-none text-slate-700"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
                />
            </div>
        );
    }

    if (block.block_type === 'video') {
        const url = String(payload.url || '');
        const caption = String(payload.caption || '');
        if (!url) return null;
        return (
            <div className="mb-8">
                <div className="bg-black rounded-lg overflow-hidden shadow-lg">
                    {url.includes('youtube.com') || url.includes('youtu.be') ? (
                        <iframe
                            src={toEmbedUrl(url)}
                            title={caption || 'Video'}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-96"
                        />
                    ) : (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center min-h-96 text-white hover:text-blue-300">
                            <span className="text-lg">Open video resource</span>
                        </a>
                    )}
                </div>
                {caption && <p className="mt-2 text-sm text-slate-500">{caption}</p>}
            </div>
        );
    }

    if (block.block_type === 'file') {
        const url = String(payload.url || '');
        const filename = String(payload.filename || 'Download file');
        if (!url) return null;
        return (
            <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-600 hover:underline">
                    📎 {filename}
                </a>
            </div>
        );
    }

    if (block.block_type === 'embed') {
        const url = String(payload.url || '');
        const provider = String(payload.provider || 'Embed');
        if (!url) return null;
        return (
            <div className="mb-8">
                <p className="text-xs font-medium text-slate-500 mb-2">{provider}</p>
                <iframe src={url} title={provider} className="w-full h-96 rounded-lg border border-slate-200" />
            </div>
        );
    }

    return null;
}

export default function LessonViewer({
    lesson,
    onComplete,
    isCompleted,
    onNext,
    onPrevious,
    onTimeUpdate,
}: LessonViewerProps) {
    const elapsedRef = useRef(0);
    const videoUrl = lesson.video_url || '';
    const blocks = (lesson.blocks || []).slice().sort((a, b) => a.order - b.order);
    const hasBlocks = blocks.length > 0;

    useEffect(() => {
        if (!onTimeUpdate) return;
        elapsedRef.current = 0;
        const interval = setInterval(() => {
            elapsedRef.current += 30;
            onTimeUpdate(elapsedRef.current);
        }, 30000);
        return () => clearInterval(interval);
    }, [lesson.id, onTimeUpdate]);

    return (
        <div className="flex-1 flex flex-col h-full bg-white overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
                <h1 className="text-xl font-bold text-gray-900">{lesson.title}</h1>
                {lesson.duration_minutes != null && (
                    <p className="mt-1 text-sm text-slate-500">{lesson.duration_minutes} min</p>
                )}
            </div>

            <div className="flex-1 p-6 max-w-4xl mx-auto w-full">
                {lesson.objectives && (
                    <div className="mb-8 rounded-lg border border-amber-100 bg-amber-50/60 px-4 py-3">
                        <h3 className="text-sm font-semibold text-amber-900 mb-1">Learning objectives</h3>
                        <pre className="whitespace-pre-wrap text-sm text-amber-900/90 font-sans">{lesson.objectives}</pre>
                    </div>
                )}

                {hasBlocks ? (
                    blocks.map((block) => <BlockRenderer key={block.id} block={block} />)
                ) : (
                    <>
                        {videoUrl && (
                            <div className="mb-8 bg-black rounded-lg overflow-hidden shadow-lg">
                                {videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be') ? (
                                    <iframe
                                        src={toEmbedUrl(videoUrl)}
                                        title={lesson.title}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        className="w-full h-96"
                                    />
                                ) : (
                                    <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center min-h-96 text-white hover:text-blue-300">
                                        <span className="text-lg">Open Video Resource</span>
                                    </a>
                                )}
                            </div>
                        )}
                        {lesson.notes && (
                            <div className="prose max-w-none text-gray-700 mb-8">
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Lesson Notes</h3>
                                <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: sanitizeHtml(lesson.notes) }} />
                            </div>
                        )}
                    </>
                )}

                <div className="flex items-center justify-between mt-12 pt-6 border-t border-gray-200">
                    <button
                        onClick={onPrevious}
                        disabled={!onPrevious}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${onPrevious ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'}`}
                    >
                        <span>Previous Lesson</span>
                    </button>

                    <button
                        onClick={onComplete}
                        className={`flex items-center space-x-2 px-6 py-2 rounded-md font-medium transition-colors ${isCompleted ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'}`}
                    >
                        {isCompleted ? <span>Completed</span> : <span>Mark as Complete</span>}
                    </button>

                    <button
                        onClick={onNext}
                        disabled={!onNext}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${onNext ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'}`}
                    >
                        <span>Next Lesson</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
